/**
 * The shop's contact details — ONE module read by the browser bundle (the /contact page, the footer)
 * and the Worker (the server-rendered /contact page, the LocalBusiness JSON-LD, GET /api/shop/config).
 *
 * ⚠️ No DOM, no React, no Worker types: imported by the Vite bundle as well. Settings reading lives
 * in `loadShopContact`, which takes only the small `query` shape both sides' databases satisfy.
 *
 * 🔴 The collection point is Shop G-09 at Dubai Mall, NOT the warehouse. The warehouse address must
 * never appear in any customer-facing output (`verify:shop-content` scans for it). The address and
 * map link come from `GET /api/shop/config → collect` when the checkout work provides it; the
 * constants below are the typed fallback.
 *
 * Values are owner-confirmed (2026-10-10): the number is the one on the Google business profile.
 * BRAND_WHATSAPP / BRAND_PHONE / BRAND_EMAIL / SHOP_HOURS in tenant_settings stay the source.
 */

/** Local form (0821234567) of a South African number, or null. A copy of the rule in phone.ts: this file is bundled for the browser and cannot import it. */
function normalisePhone(raw: string): string | null {
  const d = raw.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("27")) return `0${d.slice(2)}`;
  if (d.length === 13 && d.startsWith("0027")) return `0${d.slice(4)}`;
  if (d.length === 9 && !d.startsWith("0")) return `0${d}`;
  return d.length === 10 && d.startsWith("0") ? d : null;
}

export const WHATSAPP_CHANNEL_URL = "https://whatsapp.com/channel/0029Vb6AI4A6hENhzHO5ZK3x";
export const SOCIAL_LINKS = [
  { id: "facebook", name: "Facebook", handle: "zsmerch786", url: "https://www.facebook.com/zsmerch786" },
  { id: "instagram", name: "Instagram", handle: "@zs_merch", url: "https://www.instagram.com/zs_merch/" },
  { id: "tiktok", name: "TikTok", handle: "@zs.merch", url: "https://www.tiktok.com/@zs.merch" },
] as const;

export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
export const DAY_KEYS: readonly DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_NAMES: Record<DayKey, string> = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
/** One trading session, "HH:MM" 24-hour, SAST. A day may have several (Friday is split). */
export interface Session { open: string; close: string }
export type ShopHours = Record<DayKey, Session[]>;

export const FALLBACK_HOURS: ShopHours = {
  mon: [{ open: "09:30", close: "16:30" }],
  tue: [{ open: "09:30", close: "16:30" }],
  wed: [{ open: "09:30", close: "16:30" }],
  thu: [{ open: "09:30", close: "16:30" }],
  fri: [{ open: "09:30", close: "12:00" }, { open: "14:00", close: "16:30" }],
  sat: [{ open: "09:30", close: "16:30" }],
  sun: [{ open: "09:30", close: "14:00" }],
};

export interface ShopCollect { locationId?: string; name: string; address: string; mapUrl: string }
export const FALLBACK_COLLECT: ShopCollect = {
  name: "Z S MERCH — Shop G09",
  address: "Shop G09, Dubai Mall, 113 Industrial Rd, Amalgam, Johannesburg, 2092",
  mapUrl: "https://maps.app.goo.gl/pLT7b5XWJkD4MaQ58",
};

export interface ShopContact {
  whatsapp: string;
  phone: string;
  email: string;
}
export const FALLBACK_CONTACT: ShopContact = { whatsapp: "079 255 1394", phone: "079 255 1394", email: "info@zsmerch.co.za" };

/** What `GET /api/shop/config` carries for the contact page (all optional: older answers lack them). */
export interface ShopContactConfig {
  brand?: { whatsapp?: string | null; phone?: string | null; email?: string | null };
  /** mapUrl may be null on the wire (shop-collect.ts CollectPoint); resolveContact falls back then. */
  collect?: (Omit<ShopCollect, "mapUrl"> & { mapUrl: string | null }) | null;
  hours?: unknown;
}

/** Digits of a SA number in international form without the plus: "079 255 1394" → "27792551394". */
export function intlDigits(phone: string): string | null {
  const n = normalisePhone(phone);
  return n && /^0\d{9}$/.test(n) ? `27${n.slice(1)}` : null;
}
export const telHref = (phone: string): string | null => { const d = intlDigits(phone); return d ? `tel:+${d}` : null; };
export const waHref = (phone: string): string | null => { const d = intlDigits(phone); return d ? `https://wa.me/${d}` : null; };

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** Accepts `{mon:[{open,close}]}` or `{mon:[["09:30","16:30"]]}`; anything malformed → null (the fallback applies). */
export function normaliseHours(raw: unknown): ShopHours | null {
  let v = raw;
  if (typeof v === "string") { try { v = JSON.parse(v); } catch { return null; } }
  if (!v || typeof v !== "object") return null;
  /**
   * Two other shapes reach here and both must be read, not dropped to the fallback (which would
   * show stale hours after the owner edits them): `/api/shop/config` sends shop-collect.ts
   * `publicHours` — an array, Monday first — and the stored SHOP_HOURS setting is keyed "0"–"6"
   * with Sunday = 0 (shop-collect.ts ShopHours).
   */
  if (Array.isArray(v)) {
    if (v.length !== 7) return null;
    v = Object.fromEntries(DAY_KEYS.map((d, i) => [d, (v as Array<{ sessions?: unknown } | null>)[i]?.sessions ?? null]));
  } else if (["0", "1", "2", "3", "4", "5", "6"].some((k) => k in (v as object))) {
    const byNum = v as Record<string, unknown>;
    const sundayFirst = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
    v = Object.fromEntries(sundayFirst.map((d, i) => [d, byNum[String(i)] ?? null]));
  }
  const out = {} as ShopHours;
  for (const d of DAY_KEYS) {
    const day = (v as Record<string, unknown>)[d];
    if (day === undefined || day === null) { out[d] = []; continue; }
    if (!Array.isArray(day)) return null;
    const sessions: Session[] = [];
    for (const s of day) {
      const open = Array.isArray(s) ? s[0] : (s as Session | null)?.open;
      const close = Array.isArray(s) ? s[1] : (s as Session | null)?.close;
      if (typeof open !== "string" || typeof close !== "string" || !HHMM.test(open) || !HHMM.test(close) || close <= open) return null;
      sessions.push({ open, close });
    }
    out[d] = sessions;
  }
  return out;
}

/** The weekday key and "HH:MM" in South Africa (UTC+2, no daylight saving) for an instant. */
export function sastParts(now: Date): { day: DayKey; hhmm: string; dayIndex: number } {
  const t = new Date(now.getTime() + 2 * 3_600_000);
  const dayIndex = (t.getUTCDay() + 6) % 7; // Monday = 0
  return { day: DAY_KEYS[dayIndex]!, hhmm: `${String(t.getUTCHours()).padStart(2, "0")}:${String(t.getUTCMinutes()).padStart(2, "0")}`, dayIndex };
}

/** "Open now · closes 16:30" / "Closed · opens 09:30" / "Closed · opens Monday 09:30". */
export function openStatus(hours: ShopHours, now: Date = new Date()): { open: boolean; text: string } {
  const { day, hhmm, dayIndex } = sastParts(now);
  const today = hours[day];
  const live = today.find((s) => hhmm >= s.open && hhmm < s.close);
  if (live) return { open: true, text: `Open now · closes ${live.close}` };
  const later = today.find((s) => s.open > hhmm);
  if (later) return { open: false, text: `Closed · opens ${later.open}` };
  for (let i = 1; i <= 7; i++) {
    const k = DAY_KEYS[(dayIndex + i) % 7]!;
    const first = hours[k][0];
    if (first) return { open: false, text: `Closed · opens ${i === 1 ? "tomorrow" : DAY_NAMES[k]} ${first.open}` };
  }
  return { open: false, text: "Closed" };
}

/** One printable line per day, runs of identical days folded ("Monday – Thursday"). */
export function hoursRows(hours: ShopHours): Array<{ days: string; times: string }> {
  const fmt = (s: Session[]) => (s.length ? s.map((x) => `${x.open}–${x.close}`).join(" & ") : "Closed");
  const rows: Array<{ from: DayKey; to: DayKey; times: string }> = [];
  for (const d of DAY_KEYS) {
    const times = fmt(hours[d]);
    const last = rows[rows.length - 1];
    if (last && last.times === times) last.to = d;
    else rows.push({ from: d, to: d, times });
  }
  return rows.map((r) => ({ days: r.from === r.to ? DAY_NAMES[r.from] : `${DAY_NAMES[r.from]} – ${DAY_NAMES[r.to]}`, times: r.times }));
}

/** The Google Maps embed for an address. A `?q=` embed needs no key. */
export const mapEmbedUrl = (address: string): string => `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;

export interface ResolvedContact {
  contact: ShopContact;
  collect: ShopCollect;
  hours: ShopHours;
}

/** Config answer (any age) + fallbacks → everything the contact page prints. */
export function resolveContact(cfg: ShopContactConfig | null | undefined): ResolvedContact {
  const pick = (v: string | null | undefined, fb: string): string => (typeof v === "string" && v.trim() ? v.trim() : fb);
  /**
   * ⚠️ A configured shop with no map link still wins over the fallback: after the move to G106 the
   * fallback is the OLD shop, and sending customers there is worse than a map search of the new one.
   */
  const c = cfg?.collect;
  const collect: ShopCollect = c && c.address
    ? { ...c, mapUrl: c.mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address)}` }
    : FALLBACK_COLLECT;
  return {
    contact: {
      whatsapp: pick(cfg?.brand?.whatsapp, FALLBACK_CONTACT.whatsapp),
      phone: pick(cfg?.brand?.phone, FALLBACK_CONTACT.phone),
      email: pick(cfg?.brand?.email, FALLBACK_CONTACT.email),
    },
    collect,
    hours: normaliseHours(cfg?.hours) ?? FALLBACK_HOURS,
  };
}

/** Reads BRAND_* and SHOP_HOURS from tenant_settings — the Worker's side of `resolveContact`. */
export async function loadShopContact(db: { query<T>(sql: string, params?: unknown[]): Promise<T[]> }, collect?: ShopCollect | null): Promise<ResolvedContact> {
  const rows = await db
    .query<{ key: string; value: string }>(`select key, value from tenant_settings where key in ('BRAND_WHATSAPP', 'BRAND_PHONE', 'BRAND_EMAIL', 'SHOP_HOURS')`)
    .catch(() => [] as Array<{ key: string; value: string }>);
  const m = new Map(rows.map((r) => [r.key, r.value]));
  return resolveContact({
    brand: { whatsapp: m.get("BRAND_WHATSAPP"), phone: m.get("BRAND_PHONE"), email: m.get("BRAND_EMAIL") },
    collect: collect ?? null,
    hours: m.get("SHOP_HOURS"),
  });
}

/** schema.org LocalBusiness (Store). `url` is the shop's root; `mapUrl` becomes hasMap. */
export function localBusinessLd(r: ResolvedContact, name: string, url: string, imageUrl: string): Record<string, unknown> {
  const isDefault = r.collect.address === FALLBACK_COLLECT.address;
  const phone = intlDigits(r.contact.phone);
  const dayOfWeek: Record<DayKey, string> = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
  return {
    "@context": "https://schema.org",
    "@type": "Store",
    name,
    url,
    image: imageUrl,
    address: isDefault
      ? { "@type": "PostalAddress", streetAddress: "Shop G09, Dubai Mall, 113 Industrial Rd", addressLocality: "Amalgam, Johannesburg", addressRegion: "Gauteng", postalCode: "2092", addressCountry: "ZA" }
      : { "@type": "PostalAddress", streetAddress: r.collect.address, addressCountry: "ZA" },
    telephone: phone ? `+${phone}` : r.contact.phone,
    email: r.contact.email,
    sameAs: [...SOCIAL_LINKS.map((s) => s.url), WHATSAPP_CHANNEL_URL],
    hasMap: r.collect.mapUrl,
    openingHoursSpecification: DAY_KEYS.flatMap((d) => r.hours[d].map((s) => ({ "@type": "OpeningHoursSpecification", dayOfWeek: dayOfWeek[d], opens: s.open, closes: s.close }))),
  };
}
